import { useState } from "react";
import { Helmet } from "react-helmet";
import { gql, useMutation } from "@apollo/client";
import { useForm, FormProvider } from "react-hook-form";
import { useHistory } from "react-router";
import Button from "../../../../components/Button";
import CareTeamSidebar from "../../../../components/CareTeamSidebar";
import Layout from "../../../../components/Layout";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import ClientForm from "../../../../components/Client/Edit/";
import { Heading } from "../../../../components/Type";
import { UserType } from "../../../../types/userType";
import styles from "./index.module.scss";
import { sanitizeUpdateClientData } from "../../../../utils/forms/sanitization";

const CREATE_CLIENT = gql`
  mutation CreateClient($input: createClientInput) {
    createClient(input: $input) {
      client {
        id
      }
    }
  }
`;

const ClientNew = () => {
  const history = useHistory();
  const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);
  const [, setBoundaryError] = useState<Error | null>(null);
  const [createNewClient] = useMutation(CREATE_CLIENT, {
    onCompleted: (data) =>
      history.push(`/admin/client/${data.createClient.client.id}`),
  });

  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      cep: "false",
      sales_reps: "",
      authorizers: "",
      social_benefits: "false",
    },
  });

  const {
    state: { user },
  } = useStoreContext();

  const newClient = (values) => {
    createNewClient({
      variables: {
        input: {
          data: sanitizeUpdateClientData(values),
        },
      },
    }).catch((error) => {
      setBoundaryError(() => {
        throw error;
      });
    });
  };

  const handleSidebarSave = (users) => {
    setSelectedUsers(users);
    methods.setValue(
      "sales_reps",
      users.filter((u) => u.role.type === "sales_rep").map((u) => u.id)
    );
    methods.setValue(
      "authorizers",
      users.filter((u) => ["authorizer", "motion_support"].includes(u.role.type)).map((u) => u.id)
    );
  };

  const benefitsSelection = methods.watch("social_benefits");

  return (
    <FormProvider {...methods}>
      <Layout>
        <div className={styles.header}>
          <Helmet>
            <title>New Client</title>
          </Helmet>
          <Heading size="h3">New Client</Heading>
          <span className={styles.buttonsRight}>
            <Button
              primary
              onClick={methods.handleSubmit(newClient)}
              icon={<ArrowRight />}
              disabled={!methods.formState.isValid}
            >
              Save and Create Client
            </Button>
          </span>
        </div>
        <div className={styles.pageContent}>
          <CareTeamSidebar
            title="Care Team"
            currUser={user}
            careTeam={selectedUsers}
            editable
            onSave={handleSidebarSave}
          />
          <input type="hidden" {...methods.register("sales_reps")} />
          <input type="hidden" {...methods.register("authorizers")} />
          <ClientForm benefits={benefitsSelection === "true"} />
        </div>
      </Layout>
    </FormProvider>
  );
};

export default ClientNew;
